import {useTemplates} from "./TemplatesContext";
import {useFeedbackTools} from "../../components/FeedbackTools";
import {useEffect, useReducer, useRef, useState} from "react";
import useMountedRef from "../../hooks/useMountedRef";


const useEmailEditor = (type, templateVariables, emailLocale) => {
    // templateUserVariables is an object which keys are the name of the variable
    // and the values are their associated values. The language is assumed to be
    // selected outside.
    const [templateUserVariables, dispatcher] = useReducer(
        (state, action) => {
            switch (action.type) {
                case "update":
                    return {...state, ...action.partialState}
                case "reset":
                    return {}
                default:
                    throw new Error(`Cannot find action ${action.type} for Template reducer`);
            }
        },
        {}
    );

    const [locale, setLocale] = useState(emailLocale);

    const {
        isLoadingTemplates,
        selectedTemplateId,
        isLoadingSelectedTemplate,
        createTemplateEmailRendering,
        loadTemplates} = useTemplates();
    const {showErrorNotification} = useFeedbackTools();
    const iframeRef = useRef();
    const mounted = useMountedRef();

    // Loading status
    const [rendering, setRendering] = useState("");
    const [isRenderingLoading, setIsRenderingLoading] = useState(false);
    const [isIframeLoading, setIsIframeLoading] = useState(false);

    const onTemplateLoaded = () => {
        if (mounted.current) {
            resizeIframe();
            setIsIframeLoading(false);
        }
    }

    const resizeIframe = () => {
        iframeRef.current.style.height = iframeRef.current.contentWindow.document.body.scrollHeight + 100 + 'px';
    }

    const updateTemplateUserVariable = (partialState) => {
        resizeIframe();
        dispatcher({type: "update", partialState: partialState});
    }

    const resetTemplateUserVariable = () => dispatcher({type: "reset"});

    const handleLocaleChange = (locale) => {
        setLocale(locale);
    }

    const loadRendering = async () => {
        if (selectedTemplateId != null) {
            if (!isRenderingLoading) {
                setIsRenderingLoading(true);
            }
            if (!isIframeLoading) {
                setIsIframeLoading(true);
            }
            try {
                const data = await createTemplateEmailRendering(selectedTemplateId, {...templateVariables, locale});
                setRendering(data.emailRendering || "");  // TODO add mapper
                resetTemplateUserVariable();
                setIsRenderingLoading(false);
            } catch (e) {
                showErrorNotification(e);
                setIsRenderingLoading(false);
            }
        }
    }

    useEffect(
        () => {
            loadRendering();
        },
        [selectedTemplateId, locale]  // eslint-disable-line react-hooks/exhaustive-deps
    )

    useEffect(
        () => {
            loadTemplates({type: type})
        },
        [loadTemplates, type]
    );

    const isLoading = isLoadingTemplates || isLoadingSelectedTemplate || isRenderingLoading || isIframeLoading;
    const showIframe = !isLoadingSelectedTemplate && !isRenderingLoading;
    const isEdited = Object.keys(templateUserVariables).length > 0;

    return {
        isLoading,
        isLoadingTemplates,
        rendering,
        showIframe,
        iframeRef,
        isIframeLoading,
        templateUserVariables,
        isEdited,
        locale,
        selectedTemplateId,
        updateTemplateUserVariable,
        resetTemplateUserVariable,
        onTemplateLoaded,
        handleLocaleChange,
        loadRendering,
    }
}

export default useEmailEditor;